import React, { useEffect, useState } from 'react';
import apiClient, { formatApiError } from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import * as PhosphorIcons from '@phosphor-icons/react';

const { Palette, CheckCircle, Eye, MusicNotes, GlobeHemisphereEast } = PhosphorIcons;

const Branding = () => {
  const [branding, setBranding] = useState(null);
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [customAccent, setCustomAccent] = useState('');
  const [settings, setSettings] = useState({ timezone: 'Asia/Kolkata', city: '' });
  const [timezoneOptions, setTimezoneOptions] = useState([]);
  const [settingsSaving, setSettingsSaving] = useState(false);

  useEffect(() => { fetchBranding(); fetchSettings(); }, []);

  const fetchBranding = async () => {
    try {
      const res = await apiClient.get('/tenant/branding');
      const { presets: p, ...b } = res.data;
      setBranding(b);
      setPresets(p || []);
      setCustomAccent(b.accent_color || '#3b82f6');
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setLoading(false); }
  };

  const fetchSettings = async () => {
    try {
      const res = await apiClient.get('/tenant/settings');
      setSettings({
        timezone: res.data.timezone || 'Asia/Kolkata',
        city: res.data.city || '',
      });
      setTimezoneOptions(res.data.available_timezones || []);
    } catch (error) { /* non-fatal */ }
  };

  const saveSettings = async () => {
    setSettingsSaving(true);
    try {
      await apiClient.patch('/tenant/settings', {
        timezone: settings.timezone,
        city: settings.city,
      });
      toast.success('Region & timezone saved. Dayparts will use this zone.');
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setSettingsSaving(false); }
  };

  // Live local-time preview. Re-renders every second so the admin can verify
  // the selected timezone is correct at a glance before saving.
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => {
    const iv = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);
  const formattedLocalTime = (() => {
    try {
      const fmt = new Intl.DateTimeFormat('en-US', {
        timeZone: settings.timezone || 'Asia/Kolkata',
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
        timeZoneName: 'short',
      });
      return fmt.format(new Date(nowTick));
    } catch (e) {
      return 'Invalid timezone';
    }
  })();

  const applyPreset = (preset) => {
    setBranding(prev => ({
      ...prev,
      accent_color: preset.accent,
      background_value: preset.bg,
      text_color: preset.text,
      secondary_text: preset.text === '#ffffff' ? '#a1a1aa' : '#64748b',
    }));
    setCustomAccent(preset.accent);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put('/tenant/branding', branding);
      toast.success('Branding saved! Kiosks will update on next refresh.');
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setSaving(false); }
  };

  const updateField = (field, value) => {
    setBranding(prev => ({ ...prev, [field]: value }));
  };

  if (loading) return <div className="text-[#6b665c] font-mono">Loading...</div>;
  if (!branding) return null;

  const isDark = branding.text_color === '#ffffff';

  return (
    <div className="space-y-6 max-w-4xl" data-testid="branding-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2" data-testid="branding-title">Kiosk Branding</h1>
          <p className="text-[#6b665c] font-mono text-sm tracking-wider uppercase">Customize your in-store kiosk appearance</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white border-0" data-testid="save-branding-button">
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Region & Timezone */}
      <div className="bg-white border border-[#c5d4c8] rounded-xl p-6" data-testid="tenant-region-settings">
        <div className="flex items-center gap-3 mb-4">
          <GlobeHemisphereEast size={22} className="text-emerald-600" />
          <div>
            <h2 className="text-lg font-bold">Region & Timezone</h2>
            <p className="text-[#6b665c] font-mono text-xs mt-0.5">Kiosks evaluate dayparts and special occasions in this zone.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-[#6b665c]">City (display)</Label>
            <Input
              value={settings.city}
              onChange={(e) => setSettings((s) => ({ ...s, city: e.target.value }))}
              placeholder="e.g., Mumbai, Bengaluru, Dubai"
              className="mt-2"
              data-testid="tenant-city-input"
              maxLength={120}
            />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-[#6b665c]">Timezone (IANA)</Label>
            <Select
              value={settings.timezone}
              onValueChange={(val) => setSettings((s) => ({ ...s, timezone: val }))}
            >
              <SelectTrigger className="mt-2" data-testid="tenant-timezone-select">
                <SelectValue placeholder="Choose a timezone" />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                {timezoneOptions.map((tz) => (
                  <SelectItem key={tz.value} value={tz.value} data-testid={`tz-${tz.value.replace(/\//g, '-')}`}>
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div
          className="flex items-center justify-between gap-4 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-lg mb-4"
          data-testid="tenant-current-local-time"
        >
          <div className="flex items-center gap-2 text-[#3f4f44]">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="font-mono text-xs uppercase tracking-wider text-[#6b665c]">Current local time</span>
          </div>
          <div className="text-right">
            <p className="font-mono text-base font-semibold text-emerald-800" data-testid="tenant-local-time-display">
              {formattedLocalTime}
            </p>
            {settings.city && (
              <p className="font-mono text-[11px] text-[#6b665c] mt-0.5">{settings.city}</p>
            )}
          </div>
        </div>
        <Button
          onClick={saveSettings}
          disabled={settingsSaving}
          className="bg-emerald-600 hover:bg-emerald-700 text-white border-0"
          data-testid="save-tenant-settings-button"
        >
          {settingsSaving ? 'Saving...' : 'Save Region & Timezone'}
        </Button>
      </div>

      {/* Color Presets */}
      <div className="bg-white border border-[#c5d4c8] rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <Palette size={22} className="text-blue-500" />
          <h2 className="text-lg font-bold">Theme Presets</h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {presets.map((preset) => {
            const isActive = branding.accent_color === preset.accent && branding.background_value === preset.bg;
            return (
              <button
                key={preset.name}
                onClick={() => applyPreset(preset)}
                className={`relative rounded-xl overflow-hidden border-2 transition-all hover:scale-105 ${
                  isActive ? 'border-white shadow-lg shadow-white/10' : 'border-[#c5d4c8] hover:border-[#7c9082]'
                }`}
                data-testid={`preset-${preset.name.replace(/\s/g, '-').toLowerCase()}`}
              >
                <div className="h-20 flex items-center justify-center" style={{ background: preset.bg }}>
                  <div className="w-8 h-8 rounded-full" style={{ backgroundColor: preset.accent, boxShadow: `0 0 20px ${preset.accent}50` }} />
                </div>
                <div className="px-2 py-1.5 bg-[#f5f0e8] text-center">
                  <p className="text-[10px] font-mono text-[#6b665c] truncate">{preset.name}</p>
                </div>
                {isActive && (
                  <div className="absolute top-1 right-1">
                    <CheckCircle size={16} weight="fill" className="text-white drop-shadow-lg" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Custom Colors */}
      <div className="bg-white border border-[#c5d4c8] rounded-xl p-6">
        <h2 className="text-lg font-bold mb-4">Custom Colors</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f] mb-2 block">Accent Color</Label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={customAccent}
                onChange={(e) => { setCustomAccent(e.target.value); updateField('accent_color', e.target.value); }}
                className="w-12 h-10 rounded-md border border-[#c5d4c8] cursor-pointer bg-transparent"
                data-testid="accent-color-picker"
              />
              <Input
                value={customAccent}
                onChange={(e) => { setCustomAccent(e.target.value); updateField('accent_color', e.target.value); }}
                className="bg-[#f5f0e8] border-[#c5d4c8] text-white font-mono text-sm"
                placeholder="#3b82f6"
                data-testid="accent-color-input"
              />
            </div>
          </div>
          <div>
            <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f] mb-2 block">Background</Label>
            <Input
              value={branding.background_value}
              onChange={(e) => updateField('background_value', e.target.value)}
              className="bg-[#f5f0e8] border-[#c5d4c8] text-white font-mono text-xs"
              placeholder="CSS gradient or color"
              data-testid="bg-value-input"
            />
          </div>
          <div>
            <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f] mb-2 block">Text Color</Label>
            <div className="flex gap-2">
              <button
                onClick={() => { updateField('text_color', '#ffffff'); updateField('secondary_text', '#a1a1aa'); }}
                className={`flex-1 py-2 rounded-md text-xs font-mono border transition-all ${branding.text_color === '#ffffff' ? 'bg-zinc-700 border-white text-white' : 'bg-[#f5f0e8] border-[#c5d4c8] text-[#8a8477]'}`}
              >
                Light Text
              </button>
              <button
                onClick={() => { updateField('text_color', '#0f172a'); updateField('secondary_text', '#64748b'); }}
                className={`flex-1 py-2 rounded-md text-xs font-mono border transition-all ${branding.text_color !== '#ffffff' ? 'bg-white border-white text-black' : 'bg-[#f5f0e8] border-[#c5d4c8] text-[#8a8477]'}`}
              >
                Dark Text
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Options */}
      <div className="bg-white border border-[#c5d4c8] rounded-xl p-6">
        <h2 className="text-lg font-bold mb-4">Display Options</h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-sm">Show Track Info</p>
              <p className="text-xs text-[#8a8477]">Display title and artist on kiosk</p>
            </div>
            <Switch checked={branding.show_track_info} onCheckedChange={(v) => updateField('show_track_info', v)} data-testid="toggle-track-info" />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-sm">Show Audio Visualizer</p>
              <p className="text-xs text-[#8a8477]">Animated bars when playing</p>
            </div>
            <Switch checked={branding.show_visualizer} onCheckedChange={(v) => updateField('show_visualizer', v)} data-testid="toggle-visualizer" />
          </div>
        </div>
      </div>

      {/* Live Preview */}
      <div className="bg-white border border-[#c5d4c8] rounded-xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Eye size={20} className="text-blue-500" />
          <h2 className="text-lg font-bold">Live Preview</h2>
        </div>
        <div
          className="relative rounded-xl overflow-hidden h-64 flex flex-col items-center justify-center"
          style={{ background: branding.background_value }}
          data-testid="branding-preview"
        >
          {/* Status bar */}
          <div className="absolute top-3 left-4 flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-green-500" />
            <span style={{ color: branding.secondary_text }} className="font-mono text-[10px] uppercase tracking-wider">Connected</span>
          </div>

          {/* Track info */}
          {branding.show_track_info && (
            <div className="text-center mb-4">
              <p style={{ color: branding.secondary_text }} className="font-mono text-[10px] uppercase tracking-[0.2em] mb-2">Now Playing</p>
              <h3 className="text-3xl font-bold mb-1" style={{ color: branding.text_color }}>Summer Breeze</h3>
              <p className="text-lg font-mono" style={{ color: branding.secondary_text }}>Smooth Jazz Trio</p>
            </div>
          )}

          {/* Visualizer */}
          {branding.show_visualizer && (
            <div className="flex items-end gap-[3px] h-6 mb-3">
              {[12, 18, 10, 22, 14, 8, 16, 20].map((h, i) => (
                <div key={i} className="w-1 rounded-full animate-pulse" style={{ height: `${h}px`, backgroundColor: branding.accent_color, animationDelay: `${i * 0.1}s` }} />
              ))}
            </div>
          )}

          {/* Status */}
          <div className="absolute bottom-4 left-4 flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full animate-pulse" style={{ backgroundColor: branding.accent_color }} />
            <span style={{ color: branding.secondary_text }} className="font-mono text-xs">Playing</span>
          </div>
          <div className="absolute bottom-4 right-4">
            <span style={{ color: branding.secondary_text }} className="font-mono text-xs">Track 1 / 12</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Branding;
